import { useTranslation } from 'react-i18next'
import type { CalculateResponse } from '@itsrever/returns-api-types'
import { useTranslate } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { isBreakdownLineItem } from './breakdown-line-items'
import { LineItemLabel } from './line-item-label'

interface BalanceSectionProps {
  summary: CalculateResponse
  variant: 'refund' | 'payment'
}

export const BalanceSection = ({ summary, variant }: BalanceSectionProps) => {
  const { translate } = useTranslate()
  const { i18n } = useTranslation()

  const orderMoneyFormat =
    useAppSelector(
      (store) => store.returnsApi.order.response?.order?.money_format
    ) ?? {}

  const refundMethod =
    summary?.signed_summary?.return_summary?.refund?.refund_method

  const exchangePromotionLimit =
    summary?.signed_summary?.return_summary?.line_items?.find(
      (item) => item.subtype === 'EXCHANGE_PROMOTION_LIMIT'
    )

  const extraLineItems = summary?.signed_summary?.return_summary?.line_items
    ?.filter(isBreakdownLineItem)
    .map((item) => ({
      ...item,
      total:
        item.subtype === 'EXCHANGE_PROMOTION'
          ? (item.total ?? 0) + (exchangePromotionLimit?.total ?? 0)
          : (item.total ?? 0)
    }))

  const subTotal = summary?.calculated_summary?.subtotal_formatted

  const totalAmount =
    variant === 'refund'
      ? (summary?.signed_summary?.return_summary?.total_refund_amount ?? 0)
      : (summary?.signed_summary?.return_summary?.total_payment_amount ?? 0)

  const header =
    variant === 'refund'
      ? translate('confirmation_page_you_receive_title')
      : translate('confirmation_page_you_pay_title')

  const totalLabel =
    variant === 'refund'
      ? translate('confirmation_page_summary_total_to_receive_title')
      : translate('confirmation_page_total_to_pay_title')

  return (
    <div className="space-y-3 pt-5">
      <h3 className="text-xs font-normal uppercase tracking-wide text-neutral-500">
        {header}
      </h3>
      <dl className="space-y-1">
        <div className="flex justify-between gap-2">
          <dt
            className="text-sm font-normal text-neutral-800"
            dangerouslySetInnerHTML={{
              __html: translate('summary_subtotal')
            }}
          />
          <dd className="text-sm font-normal text-neutral-800">{subTotal}</dd>
        </div>
        {extraLineItems?.map((item, index) => {
          if (variant === 'refund' && item.checkout_payment_item) {
            return null
          }

          return (
            // Keyed by position: an order can carry one external refund line per returned item,
            // so the subtype is no longer unique within the list.
            <div
              className="flex justify-between gap-2"
              key={`${item.subtype}-${index}`}
            >
              <LineItemLabel
                subtype={item.subtype ?? ''}
                name={item.name}
                refundMethod={refundMethod}
              />
              <dd className="text-sm font-normal text-neutral-800">
                {(
                  (item.total ?? 0) /
                  (orderMoneyFormat.amount_multiplied_by ?? 100)
                ).toLocaleString(i18n.language, {
                  style: 'currency',
                  currency: orderMoneyFormat.currency
                })}
              </dd>
            </div>
          )
        })}
        <div className="flex justify-between gap-2 pt-0.5">
          <dt className="text-sm font-medium text-neutral-800">{totalLabel}</dt>
          <dd className="text-base font-medium text-neutral-800">
            {(
              totalAmount / (orderMoneyFormat.amount_multiplied_by ?? 100)
            ).toLocaleString(i18n.language, {
              style: 'currency',
              currency: orderMoneyFormat.currency
            })}
          </dd>
        </div>
      </dl>
    </div>
  )
}
